import {
  QBankStructureLevelCategory,
  type GetQBankStructureLevelItemsByQBankStructureIdQuery,
  type GetQBankStructureLevelsByQBankStructureIdQuery,
} from '@exam-management/graphql';
import {
  findNextEnabledLevel,
  type QBankStructureLevelItemWithLevel,
} from '@exam-management/qbs-provider';
import type { CheckboxData } from '@shared/hooks';

export type QBankStructureLevelItemData =
  GetQBankStructureLevelItemsByQBankStructureIdQuery['qbsGetQBankStructureLevelItemsByQBankStructureId'][0];

export type QBankStructureLevelData =
  GetQBankStructureLevelsByQBankStructureIdQuery['qbsGetQBankStructureLevelsByQBankStructureId'][0];

const interestLevels = [
  QBankStructureLevelCategory.Part,
  QBankStructureLevelCategory.Subject,
  QBankStructureLevelCategory.Group,
  QBankStructureLevelCategory.Chapter,
];

function mapLevelItemByLevelId(
  levelItems: QBankStructureLevelItemWithLevel[],
  levels: QBankStructureLevelData[],
) {
  const levelItemsMapByLevelId = new Map<string, QBankStructureLevelItemWithLevel[]>();

  for (const level of levels) {
    const items = [...levelItems.filter((item) => item.qBankStructureLevelId === level.id)];

    levelItemsMapByLevelId.set(level.id, items);
  }

  return levelItemsMapByLevelId;
}

function mapLevelItemByParentId(
  levelItemsMapByLevelId: Map<string, QBankStructureLevelItemWithLevel[]>,
) {
  const levelItemsMapByParentId = new Map<string, QBankStructureLevelItemWithLevel[]>();

  for (const [, items] of levelItemsMapByLevelId) {
    for (const item of items) {
      const parentId = item.parentLevelItemId;
      if (parentId) {
        if (!levelItemsMapByParentId.has(parentId)) {
          levelItemsMapByParentId.set(parentId, []);
        }

        const siblings = levelItemsMapByParentId.get(parentId);
        if (siblings) {
          const newItems = [...siblings, item].sort((a, b) => a.order - b.order);

          levelItemsMapByParentId.set(parentId, newItems);
        }
      }
    }
  }

  return levelItemsMapByParentId;
}

function getLevelItemById(levelItems: QBankStructureLevelItemWithLevel[], id: string | null) {
  if (!id) {
    return null;
  }

  return levelItems.find((item) => item.id === id);
}

function sortLevelItemByParentOrder(
  currentLevelItem: QBankStructureLevelItemWithLevel,
  candidateLevelItem: QBankStructureLevelItemWithLevel,
  levelItems: QBankStructureLevelItemWithLevel[],
) {
  if (currentLevelItem.parentLevelItemId === candidateLevelItem.parentLevelItemId) {
    return currentLevelItem.order - candidateLevelItem.order;
  } else {
    const currentParentLevelItem = getLevelItemById(levelItems, currentLevelItem.parentLevelItemId);
    const candidateParentLevelItem = getLevelItemById(
      levelItems,
      candidateLevelItem.parentLevelItemId,
    );

    if (currentParentLevelItem && candidateParentLevelItem) {
      return currentParentLevelItem.order - candidateParentLevelItem.order;
    }

    return 0;
  }
}

function replaceGroupLevelChildrenWithItemsOfPreviousLevel(
  levelItemsMapByLevelId: Map<string, QBankStructureLevelItemWithLevel[]>,
  levels: QBankStructureLevelData[],
  groupLevelId: string,
) {
  const groupLevelItems = levelItemsMapByLevelId.get(groupLevelId ?? '');
  const nextLevelOfGroup = findNextEnabledLevel(levels, groupLevelId);

  if (nextLevelOfGroup && groupLevelItems) {
    const nextLevelItems = levelItemsMapByLevelId.get(nextLevelOfGroup?.id ?? '');

    if (nextLevelItems) {
      const newChildren = createNewChildren(groupLevelItems, nextLevelItems);
      levelItemsMapByLevelId.set(groupLevelId, newChildren);
      levelItemsMapByLevelId.delete(nextLevelOfGroup.id);
    }
  }
}

function createNewChildren(
  groupLevelItems: QBankStructureLevelItemWithLevel[],
  nextLevelItems: QBankStructureLevelItemWithLevel[],
) {
  const newChildren = [];
  for (const item of groupLevelItems) {
    const children = nextLevelItems
      .filter((nextItem) => nextItem.parentLevelItemId === item.id)
      .sort((a, b) => sortLevelItemByParentOrder(a, b, nextLevelItems));

    if (children.length) {
      for (const child of children) {
        newChildren.push({
          ...child,
          parentLevelItemId: item.parentLevelItemId,
          order: newChildren.length + 1,
        });
      }
    }
  }
  return newChildren;
}

function getFirstLevelItems(
  firstLevel: QBankStructureLevelData,
  levelItemsMapByLevelId: Map<string, QBankStructureLevelItemWithLevel[]>,
  firstLevelItem: QBankStructureLevelItemData,
) {
  if (firstLevel.category === QBankStructureLevelCategory.Chapter) {
    const mockParentForChapter = {
      id: firstLevelItem.parentLevelItemId ?? 'mock-parent-for-chapter',
      qBankStructureLevelId: firstLevel.id,
      qBankStructureLevel: firstLevel,
      parentLevelItemId: null,
      qBankStructureId: firstLevelItem.qBankStructureId,
      order: 1,
      tagId: 'mock-parent-for-chapter',
      tag: {
        id: 'mock-parent-for-chapter',
        name: 'รายละเอียดบท',
        slug: null,
        description: null,
        category: null,
      },
    };

    return [mockParentForChapter];
  } else {
    return [...(levelItemsMapByLevelId.get(firstLevel.id) ?? []).sort((a, b) => a.order - b.order)];
  }
}

export function formatQBankStructureData(
  levels: QBankStructureLevelData[],
  levelItems: QBankStructureLevelItemData[],
) {
  if (!levels.length || !levelItems.length) {
    return {
      firstLevelItems: [],
      levelItems: new Map<string, QBankStructureLevelItemWithLevel[]>(),
    };
  }

  const filledLevelItems: QBankStructureLevelItemWithLevel[] = levelItems
    .map((item) => ({
      ...item,
      qBankStructureLevel: levels.find((level) => level.id === item.qBankStructureLevelId),
    }))
    .filter((item) => item.qBankStructureLevel !== undefined) as QBankStructureLevelItemWithLevel[];

  const sortedLevels = levels
    .filter((level) => interestLevels.includes(level.category) && level.enabled)
    .sort((a, b) => a.level - b.level);

  // map level items by level id
  const levelItemsMapByLevelId = mapLevelItemByLevelId(filledLevelItems, sortedLevels);

  // replace children of group level with items of previous level
  const groupLevelId = sortedLevels.find(
    (level) => level.category === QBankStructureLevelCategory.Group,
  )?.id;

  if (groupLevelId) {
    replaceGroupLevelChildrenWithItemsOfPreviousLevel(
      levelItemsMapByLevelId,
      sortedLevels,
      groupLevelId,
    );
  }

  const levelItemsMapByParentId = mapLevelItemByParentId(levelItemsMapByLevelId);

  const firstLevelItems = getFirstLevelItems(
    sortedLevels[0],
    levelItemsMapByLevelId,
    levelItems[0],
  );

  return {
    firstLevelItems,
    levelItems: levelItemsMapByParentId,
  };
}

export function getCheckboxData(
  data: Map<string, QBankStructureLevelItemWithLevel[]>,
): CheckboxData[] {
  const getChildren = (item: QBankStructureLevelItemWithLevel): CheckboxData[] | undefined => {
    const children = data.get(item.id);

    if (!children) {
      return;
    }

    return children.map((child) => ({
      name: child.id,
      children: getChildren(child),
    }));
  };

  const topLevelDataKeys = Array.from(data.keys()).filter((key) => {
    return !Array.from(data.values())
      .flat()
      .some((item) => item.id === key);
  });

  return topLevelDataKeys.map((key) => ({
    name: key,
    children: data.get(key)?.map((item) => ({
      name: item.id,
      children: getChildren(item),
    })),
  }));
}
